import type { ReactNode } from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, YAxis } from 'recharts';
import { useLocale } from '../../i18n/LocaleProvider';
import type { OpsInfrastructureLevel } from '../../lib/infrastructure-api';
import {
  formatClock,
  LEVEL_COLOURS,
  levelLabelKey,
  levelTone,
  meterWidth,
  ringDash,
} from '../../lib/infrastructure';
import { TOOLTIP_STYLE } from '../chartTheme';
import { Badge } from '../Pill';

/**
 * The Infrastruktur page's building blocks, in the dashboard's own vocabulary: `.surface` panels
 * with the operations dashboard's section heading, `Badge` for every level (the word carries the
 * meaning, the tint only supports it), the shared chart tooltip, and the brand teal as the one
 * accent. The ring gauge and the meter are the only marks this page adds; both use the level's
 * Tailwind step on a `-100` track of the same hue.
 */

/** A panel with the operations dashboard's heading pattern (title, optional lead, optional aside). */
export function Section({
  title,
  lead,
  aside,
  children,
  className = '',
}: {
  readonly title: string;
  readonly lead?: string;
  readonly aside?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return (
    <section className={`surface p-5 space-y-4 min-w-0 ${className}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          {lead && <p className="text-xs text-gray-500 max-w-3xl">{lead}</p>}
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** A level as the dashboard's tinted badge. */
export function LevelBadge({
  level,
}: {
  readonly level: OpsInfrastructureLevel;
}) {
  const { t } = useLocale();
  return <Badge tone={levelTone(level)}>{t(levelLabelKey(level))}</Badge>;
}

/** A ring gauge: the arc is the percentage, its colour the level, the figure in the middle ink. */
export function RingGauge({
  label,
  percent,
  display,
  level,
  detail,
}: {
  readonly label: string;
  readonly percent: number | null;
  readonly display: string;
  readonly level: OpsInfrastructureLevel;
  readonly detail?: string;
}) {
  const { t } = useLocale();
  const radius = 34;
  const { circumference, offset } = ringDash(percent, radius);
  const colour = LEVEL_COLOURS[level];
  return (
    <figure className="flex min-w-0 flex-col items-center gap-1">
      <div className="relative h-20 w-20 sm:h-[5.5rem] sm:w-[5.5rem]">
        <svg
          viewBox="0 0 80 80"
          className="h-full w-full -rotate-90"
          role="img"
          aria-label={`${label}: ${display} — ${t(levelLabelKey(level))}`}
        >
          <circle
            cx="40"
            cy="40"
            r={radius}
            fill="none"
            stroke={colour.track}
            strokeWidth="7"
          />
          <circle
            cx="40"
            cy="40"
            r={radius}
            fill="none"
            stroke={colour.mark}
            strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="motion-safe:transition-[stroke-dashoffset] motion-safe:duration-500"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-base font-bold text-gray-900">{display}</span>
        </div>
      </div>
      <figcaption className="text-xs font-medium text-gray-700">
        {label}
      </figcaption>
      {detail && (
        <span className="text-center text-[11px] leading-tight text-gray-500">
          {detail}
        </span>
      )}
      <LevelBadge level={level} />
    </figure>
  );
}

/** A horizontal meter on a track of the same hue. */
export function Meter({
  percent,
  level,
  label,
}: {
  readonly percent: number | null;
  readonly level: OpsInfrastructureLevel;
  readonly label: string;
}) {
  const colour = LEVEL_COLOURS[level];
  const value =
    percent === null ? undefined : Math.min(100, Math.max(0, percent));
  return (
    <div
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={value}
      className="h-2 w-full overflow-hidden rounded-full"
      style={{ background: colour.track }}
    >
      <div
        className="h-full rounded-full motion-safe:transition-[width] motion-safe:duration-500"
        style={{ width: meterWidth(percent), background: colour.mark }}
      />
    </div>
  );
}

/** A small labelled figure, in the KPI tiles' label style. */
export function Stat({
  label,
  value,
  sub,
}: {
  readonly label: string;
  readonly value: ReactNode;
  readonly sub?: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </dt>
      <dd className="mt-0.5 truncate text-sm font-semibold text-gray-900">
        {value}
      </dd>
      {sub && <dd className="truncate text-xs text-gray-500">{sub}</dd>}
    </div>
  );
}

/** One point of a sparkline. */
export interface SparkPoint {
  readonly t: number;
  readonly value: number | null;
}

/**
 * A single-series sparkline with the dashboard's chart tooltip (time + value). One series, so no
 * legend — the title names it. Gaps stay gaps; nothing is interpolated into a fake reading.
 */
export function Sparkline({
  title,
  points,
  format,
  max,
}: {
  readonly title: string;
  readonly points: readonly SparkPoint[];
  readonly format: (value: number) => string;
  /** Fixed top of the scale (100 for percentages); auto when omitted. */
  readonly max?: number;
}) {
  const { t, locale } = useLocale();
  const colour = LEVEL_COLOURS.ok.mark;
  const gradientId = `spark-${title.replace(/[^a-zA-Z0-9]/g, '')}`;
  const data = points.map((point) => ({ t: point.t, value: point.value }));
  return (
    <figure className="min-w-0">
      <figcaption className="mb-1 text-xs text-gray-500">{title}</figcaption>
      {data.length < 2 ? (
        <div className="h-12 rounded-md bg-gray-50" />
      ) : (
        <div className="h-12">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{ top: 2, right: 0, bottom: 0, left: 0 }}
            >
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={colour} stopOpacity={0.22} />
                  <stop offset="95%" stopColor={colour} stopOpacity={0} />
                </linearGradient>
              </defs>
              <YAxis hide domain={[0, max ?? 'auto']} />
              <Tooltip
                cursor={{ stroke: '#d1d5db', strokeWidth: 1 }}
                content={({ active, payload }) => {
                  const entry = payload?.[0];
                  if (!active || !entry) return null;
                  const raw: unknown = entry.value;
                  const time: unknown = entry.payload?.t;
                  if (typeof raw !== 'number' || typeof time !== 'number')
                    return null;
                  return (
                    <div
                      style={TOOLTIP_STYLE}
                      className="px-2.5 py-1.5 text-xs text-gray-700"
                    >
                      {t('infrastructure.chart.value', {
                        value: format(raw),
                        time: formatClock(time, locale),
                      })}
                    </div>
                  );
                }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={colour}
                strokeWidth={2}
                fill={`url(#${gradientId})`}
                connectNulls={false}
                isAnimationActive={false}
                dot={false}
                activeDot={{
                  r: 4,
                  stroke: '#ffffff',
                  strokeWidth: 2,
                  fill: colour,
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </figure>
  );
}
